/**
 * Shipping Calculator Module
 * Refatorado para separar responsabilidades: Constantes, Utils, API e UI.
 */
(() => {
  const $root = document.getElementById('shipping-calculator');
  if (!$root || $root.dataset.enhanced === 'true') return;
  $root.dataset.enhanced = 'true';

  // ===========================================================================
  // 1. CONSTANTES (UPPER_SNAKE_CASE)
  // ===========================================================================
  const CONFIG = {
    MONEY_FORMAT: JSON.parse($root.getAttribute('data-money-format') || '"${{amount}}"'),
    DEFAULT_ISO: ($root.getAttribute('data-default-country') || 'BR').toUpperCase(),
    POLL_MAX_ATTEMPTS: 10,
    POLL_BASE_INTERVAL_MS: 150,
    STOREFRONT_API_URL: `${window.Shopify?.routes?.root || '/'}api/2026-07/graphql.json`,
  };

  const CART_CREATE_MUTATION = `
    mutation ShippingEstimate($input: CartInput!, $country: CountryCode) @inContext(country: $country) {
      cartCreate(input: $input) {
        cart { id }
        userErrors { message }
      }
    }`;

  // withCarrierRates (fretes de transportadora) só é aceito dentro de @defer
  const CART_QUERY = `
    query ShippingEstimateRates($id: ID!, $country: CountryCode) @inContext(country: $country) {
      cart(id: $id) {
        id
        ... @defer {
          deliveryGroups(first: 10, withCarrierRates: true) {
            nodes {
              deliveryOptions { title description estimatedCost { amount } }
            }
          }
        }
      }
    }`;

  const FREE_SHIPPING_LABELS = ['R$:0,00', 'R$ 0,00', 'R$0,00'];

  // ===========================================================================
  // 2. ELEMENTOS DO DOM
  // ===========================================================================
  const DOM = {
    zipInput: document.getElementById('address_zip'),
    btnSubmit: document.getElementById('get-rates-submit'),
    wrapper: document.getElementById('wrapper-response'),
    inlineBox: document.getElementById('shipping-inline'),
    ctaBox: document.getElementById('shipping-inline-cta'),
    styleWrap: document.getElementById('wrapper-aux--style'),
    locationEl: document.getElementById('shipping-location'),
    locationText: document.getElementById('shipping-location-text'),
    modal: document.getElementById('shipping-modal'),
    modalBody: document.querySelector('#shipping-modal .shipping-modal__body'),
  };

  // ===========================================================================
  // 3. UTILS (Funções puras e genéricas)
  // ===========================================================================
  const sleepAsync = (ms) => new Promise(r => setTimeout(r, ms));
  const sanitizeCep = (value) => (value || '').replace(/\D+/g, '').slice(0, 8);
  
  const formatCep = (value) => {
    const digits = sanitizeCep(value);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  // Respostas com @defer chegam como multipart/mixed: um JSON por parte
  const parseGraphqlPayloads = (text, contentType) => {
    const boundary = /boundary="?([^";]+)"?/i.exec(contentType || '')?.[1];
    if (!boundary) return [JSON.parse(text)];

    return text.split(`--${boundary}`).reduce((payloads, part) => {
      const start = part.indexOf('{');
      const end = part.lastIndexOf('}');
      if (start === -1 || end <= start) return payloads;
      try { payloads.push(JSON.parse(part.slice(start, end + 1))); } catch { /* parte sem JSON */ }
      return payloads;
    }, []);
  };

  const findKey = (node, key) => {
    if (!node || typeof node !== 'object') return undefined;
    if (!Array.isArray(node) && key in node) return node[key];
    for (const child of Object.values(node)) {
      const found = findKey(child, key);
      if (found !== undefined) return found;
    }
    return undefined;
  };

  const toCents = (value) => {
    if (typeof value === 'number') return Number.isInteger(value) ? value : Math.round(value * 100);
    if (typeof value !== 'string') return 0;
    
    const stringValue = value.trim();
    if (/^\d+$/.test(stringValue)) return parseInt(stringValue, 10);
    
    // Trata formatos '1.200,50' ou '1,200.50' ou '10,50'
    const normalized = stringValue.includes(',') && stringValue.includes('.')
      ? stringValue.replace(/\./g, '').replace(',', '.')
      : stringValue.replace(',', '.');
      
    const floatValue = parseFloat(normalized);
    return Number.isFinite(floatValue) ? Math.round(floatValue * 100) : 0;
  };

  const formatShopifyMoney = (input, customFormat) => {
    const cents = toCents(input);
    const format = customFormat || CONFIG.MONEY_FORMAT || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (numberInCents, precision = 2, thousands = ',', decimal = '.') => {
      const num = (numberInCents / 100).toFixed(precision);
      const [head, tailPart] = num.split('.');
      const formattedHead = head.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return tailPart ? `${formattedHead}${decimal}${tailPart}` : formattedHead;
    };

    const match = format.match(placeholderRegex);
    const token = match ? match[1] : 'amount';

    const tokenMap = {
      'amount': () => formatWithDelimiters(cents, 2, ',', '.'),
      'amount_no_decimals': () => formatWithDelimiters(cents, 0, ',', '.'),
      'amount_with_comma_separator': () => formatWithDelimiters(cents, 2, '.', ','),
      'amount_no_decimals_with_comma_separator': () => formatWithDelimiters(cents, 0, '.', ','),
      'amount_with_apostrophe_separator': () => formatWithDelimiters(cents, 2, "'", '.'),
    };

    const formattedValue = (tokenMap[token] || tokenMap['amount'])();
    return format.replace(placeholderRegex, formattedValue);
  };

  const getEtaText = (deliveryDays) => {
    if (!Array.isArray(deliveryDays) || deliveryDays.length !== 2 || !deliveryDays[0]) return '';
    const [min, max] = deliveryDays;
    const isSameDay = Number(min) === Number(max);
    const plural = (n) => Number(n) === 1 ? 'dia útil' : 'dias úteis';
    return isSameDay ? `${min} ${plural(min)}.` : `${min} a ${max} ${plural(max)}.`;
  };

  // ===========================================================================
  // 4. SERVIÇOS / API (Comunicação externa)
  // ===========================================================================
  const ApiService = {
    async fetchViaCep(zip) {
      if (CONFIG.DEFAULT_ISO !== 'BR') return { data: null, province: '' };
      try {
        const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
        if (!response.ok) throw new Error('Falha na resposta do ViaCEP');
        const data = await response.json();
        return (data && !data.erro) ? { data, province: data.uf || '' } : { data: null, province: '' };
      } catch (error) {
        console.warn('[Shipping] Erro ao buscar ViaCEP:', error);
        return { data: null, province: '' };
      }
    },

    async fetchCartItems() {
      const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error('Erro ao consultar o carrinho.');
      const cart = await response.json();
      return Array.isArray(cart.items) ? cart.items : [];
    },

    async storefrontRequest(query, variables) {
      const response = await fetch(CONFIG.STOREFRONT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, multipart/mixed' },
        body: JSON.stringify({ query, variables })
      });
      if (!response.ok) throw new Error('Erro ao calcular frete.');

      const payloads = parseGraphqlPayloads(await response.text(), response.headers.get('Content-Type'));
      if (!payloads.some(payload => payload.data || payload.incremental || payload.path)) {
        throw new Error(payloads[0]?.errors?.[0]?.message || 'Erro ao calcular frete.');
      }
      return payloads;
    },

    extractRates(payloads) {
      const groups = payloads.map(payload => findKey(payload, 'deliveryGroups')?.nodes || []).flat();
      const group = groups.find(g => g.deliveryOptions?.length);
      if (!group) return [];
      return group.deliveryOptions.map(option => ({
        name: option.title,
        price: Math.round(parseFloat(option.estimatedCost?.amount || 0) * 100),
        description: option.description || ''
      }));
    },

    /**
     * Cota o frete em um carrinho temporário (Storefront API), espelhando os itens do
     * carrinho real. As rotas /cart/*shipping_rates.json gravam o CEP no carrinho do
     * cliente e ele chega preenchido no checkout, o que impede o autopreenchimento
     * de endereço de lá.
     */
    async fetchRates({ items, zip, province, viaCep }) {
      const deliveryAddress = { countryCode: CONFIG.DEFAULT_ISO, zip };
      if (province) deliveryAddress.provinceCode = province;
      if (viaCep?.localidade) deliveryAddress.city = viaCep.localidade;
      if (viaCep?.logradouro) deliveryAddress.address1 = viaCep.logradouro;

      const lines = items.map(item => {
        const line = { merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity };
        const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlanId) line.sellingPlanId = `gid://shopify/SellingPlan/${sellingPlanId}`;
        return line;
      });

      const input = {
        lines,
        delivery: { addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress } }] }
      };

      const created = await ApiService.storefrontRequest(CART_CREATE_MUTATION, { input, country: CONFIG.DEFAULT_ISO });
      const { cart, userErrors } = findKey(created, 'cartCreate') || {};
      if (!cart) throw new Error(userErrors?.[0]?.message || 'Erro ao calcular frete.');

      let rates = [];

      // Fretes calculados por transportadora podem demorar a ficar disponíveis
      for (let i = 0; !rates.length && i < CONFIG.POLL_MAX_ATTEMPTS; i++) {
        if (i > 0) await sleepAsync(CONFIG.POLL_BASE_INTERVAL_MS + i * 50);
        const polled = await ApiService.storefrontRequest(CART_QUERY, { id: cart.id, country: CONFIG.DEFAULT_ISO });
        rates = ApiService.extractRates(polled);
      }

      return rates;
    }
  };

  // ===========================================================================
  // 5. MANIPULAÇÃO DE UI (Handlers e Renders)
  // ===========================================================================
  const UI = {
    setLoading(isLoading) {
      if (!DOM.btnSubmit) return;
      DOM.btnSubmit.disabled = isLoading;
      DOM.btnSubmit.textContent = isLoading ? 'Calculando…' : 'Calcular frete';
      if (isLoading) {
        DOM.wrapper.innerHTML = '<div class="shipping-loader" aria-label="Carregando"><span></span><span></span><span></span></div>';
      } else {
        const loader = DOM.wrapper.querySelector('.shipping-loader');
        if (loader) loader.remove();
      }
    },

    renderFeedback(isSuccess, message) {
      UI.setLoading(false);
      DOM.wrapper.innerHTML = `<p id="shipping-rates-feedback" class="${isSuccess ? 'success' : 'error'}">${message}</p>`;
    },

    renderLocation(data) {
      if (!DOM.locationEl || !DOM.locationText || !data || data.erro) {
        if (DOM.locationEl) DOM.locationEl.hidden = true;
        return;
      }
      const addressParts = [data.logradouro, data.bairro ? `${data.bairro} -` : '', data.localidade, data.uf ? `- ${data.uf}` : ''];
      DOM.locationText.textContent = addressParts.filter(Boolean).join(' ');
      DOM.locationEl.hidden = false;
    },

    clearInline() {
      DOM.styleWrap?.classList.remove('has-bg');
      if (DOM.inlineBox) { DOM.inlineBox.hidden = true; DOM.inlineBox.innerHTML = ''; }
      if (DOM.ctaBox) { DOM.ctaBox.hidden = true; DOM.ctaBox.innerHTML = ''; }
    },

    renderInlineBestOnly(rates) {
      UI.clearInline();
      if (!Array.isArray(rates) || !rates.length) return;

      const sorted = [...rates].sort((a, b) => toCents(a.price) - toCents(b.price));
      const best = sorted[0];
      const name = best.name || 'Frete';
      const formattedPrice = formatShopifyMoney(best.price);
      
      const finalPrice = FREE_SHIPPING_LABELS.includes(formattedPrice.replace(/\s/g, '')) ? 'GRÁTIS' : formattedPrice;
      const etaText = getEtaText(best.delivery_days) || best.description || '';

      DOM.inlineBox.innerHTML = `
        <div class="shipping-inline__row">
          <span class="shipping-inline__name">${name}</span>
          <span class="shipping-inline__price">${finalPrice}</span>
        </div>
        ${etaText ? `<p class="shipping-inline__eta">${etaText}</p>` : ''}`;
      
      DOM.inlineBox.hidden = false;
      DOM.styleWrap?.classList.add('has-bg');

      if (rates.length > 1) {
        DOM.ctaBox.innerHTML = `<button type="button" class="shipping-inline__more-outside" aria-haspopup="dialog" aria-controls="shipping-modal">Ver outras opções</button>`;
        DOM.ctaBox.hidden = false;
        DOM.ctaBox.querySelector('button').addEventListener('click', () => {
          const feedback = DOM.wrapper.querySelector('#shipping-rates-feedback');
          UI.openModal(`${feedback ? feedback.outerHTML : ''}${UI.buildRatesListHTML(sorted)}`);
        });
      }
    },

    buildRatesListHTML(rates) {
      const itemsHtml = rates.map(rate => {
        const name = rate.name || 'Frete';
        const price = formatShopifyMoney(rate.price);
        const finalPrice = FREE_SHIPPING_LABELS.includes(price.replace(/\s/g, '')) ? 'GRÁTIS' : price;
        const etaText = getEtaText(rate.delivery_days) || rate.description || '';
        
        return `<li>
                  <div class="ship-info">
                    <span class="ship-name">${name}</span>
                    ${etaText ? `<span class="ship-eta">${etaText}</span>` : ''}
                  </div>
                  <span class="ship-price">${finalPrice}</span>
                </li>`;
      }).join('');
      return `<ul id="shipping-rates" style="list-style:none;padding:0;margin:8px 0 0">${itemsHtml}</ul>`;
    },

    openModal(htmlContent) {
      DOM.modalBody.innerHTML = htmlContent;
      DOM.modal.classList.add('is-open');
      DOM.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      DOM.modal.querySelector('.shipping-modal__dialog')?.focus();
    },

    closeModal() {
      DOM.modal.classList.remove('is-open');
      DOM.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
    }
  };

  // ===========================================================================
  // 6. CONTROLADOR PRINCIPAL (Fluxo de Negócio)
  // ===========================================================================
  async function handleCalculate() {
    const rawZip = DOM.zipInput.value;
    const zip = sanitizeCep(rawZip);
    
    if (!zip || zip.length < 5) {
      UI.renderFeedback(false, 'Informe um CEP válido.');
      UI.clearInline();
      UI.renderLocation(null);
      return;
    }

    try {
      // 1. Limpa resquícios da pesquisa anterior PRIMEIRO
      DOM.wrapper.innerHTML = '';
      UI.clearInline();
      UI.renderLocation(null);
      
      // 2. AGORA sim, injeta o loading na tela limpa
      UI.setLoading(true);

      const [items, viaCepResponse] = await Promise.all([
        ApiService.fetchCartItems(),
        ApiService.fetchViaCep(zip)
      ]);

      if (!items.length) {
        UI.renderFeedback(false, 'Adicione um produto ao carrinho para calcular o frete.');
        return;
      }

      UI.renderLocation(viaCepResponse.data);

      const rates = await ApiService.fetchRates({
        items,
        zip,
        province: viaCepResponse.province,
        viaCep: viaCepResponse.data
      });

      if (!rates.length) {
        UI.renderFeedback(false, 'Não há opções de frete para este CEP.');
        return;
      }

      UI.renderInlineBestOnly(rates);
    } catch (error) {
      UI.renderFeedback(false, error.message || 'Ocorreu um erro ao calcular o frete.');
    } finally {
      // 3. O loading é removido aqui, no finally, quando tudo acaba
      UI.setLoading(false);
    }
  }

  // ===========================================================================
  // 7. EVENT LISTENERS INIT
  // ===========================================================================
  if (DOM.zipInput) {
    DOM.zipInput.value = formatCep(DOM.zipInput.value);
    DOM.zipInput.addEventListener('input', (e) => e.target.value = formatCep(e.target.value));
    DOM.zipInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); handleCalculate(); }
    });
  }

  if (DOM.btnSubmit) DOM.btnSubmit.addEventListener('click', handleCalculate);

  if (DOM.modal) {
    DOM.modal.addEventListener('click', (e) => {
      if (e.target.matches('[data-close], .shipping-modal__backdrop') || !e.target.closest('.shipping-modal__dialog')) {
        UI.closeModal();
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && DOM.modal?.classList.contains('is-open')) UI.closeModal();
  });

})();